import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { City, CityWithTimes, TrainRoute, TripLeg, DaySegment, StopActions } from "../data/types";
import { trainRouteToTripLeg } from "../data/types";
import { generateTripPdf } from "../components/generateTripPdf";
import { resolveRouteCoords, resolveCity } from "../services/cityStore";
import { useGhostLegs } from "./useGhostLegs";
import { calendarDaysBetween, addCalendarDays, stationDateTime, nextDeparture, legArrivalInstant, journeyInstant } from "../services/journeyTime";
import { DAY_COLORS } from "../data/design-tokens";

// ─── Module-scope Constants & Pure Functions ─────────────────────────────

const DEFAULT_STOP_ACTIONS = { food: false, overnight: false, fika: false } as const;

/**
 * Enrich a HAFAS City with Supabase data (description, image, POI fields, etc.).
 * Merges non-empty Supabase fields into the original, keeping HAFAS lat/lon/id.
 * Runs async but never blocks the UI — stop is added immediately, enriched in-place.
 */
async function enrichCity(city: City): Promise<City> {
    const resolved = await resolveCity(city.city);
    if (!resolved) return city;
    return {
        ...city,
        description: city.description || resolved.description,
        poiType: city.poiType || resolved.poiType,
        poiName: city.poiName || resolved.poiName,
        imageUrl: city.imageUrl || resolved.imageUrl,
        poiDescription: city.poiDescription || resolved.poiDescription,
        poiShowcaseText: city.poiShowcaseText || resolved.poiShowcaseText,
        poiReason: city.poiReason || resolved.poiReason,
        spotifyUri: city.spotifyUri || resolved.spotifyUri,
        attractions: city.attractions?.length ? city.attractions : resolved.attractions,
        foodSpecialties: city.foodSpecialties?.length ? city.foodSpecialties : resolved.foodSpecialties,
        country: city.country || resolved.country,
        population: city.population || resolved.population,
    };
}

async function enrichTripStop(station: City, setStops: React.Dispatch<React.SetStateAction<City[]>>): Promise<void> {
  const enriched = await enrichCity(station);
  if (enriched !== station) setStops(stops => stops.map(stop => stop.id === enriched.id ? enriched : stop));
}

/** True if the leg's arrival date is after its departure date (HAFAS ISO dates). */
function isOvernightLeg(leg: Pick<TripLeg, "departureDate" | "arrivalDate">): boolean {
  return !!leg.departureDate && !!leg.arrivalDate && leg.arrivalDate > leg.departureDate;
}

/** Nights at the stop and calendar days spent waiting for its departure overlap. */
function departureDayAdvance(leg: TripLeg | undefined, previous: TripLeg | undefined, actions: StopActions[string] | undefined, travelDate: string): number {
  const nights = actions?.overnight ? actions.nights || 1 : 0;
  const arrivalDate = previous?.arrivalDate ?? travelDate;
  const waitingDays = leg?.departureDate ? calendarDaysBetween(arrivalDate, leg.departureDate) : 0;
  return Math.max(nights, waitingDays);
}

function staleLegReasons(leg: TripLeg, previous: TripLeg | undefined, from: City, day: number, actions: StopActions, travelDate: string): string[] {
  const reasons: string[] = [];
  if (leg.departureDate) {
    const plannedDate = addCalendarDays(travelDate, day - 1);
    const nights = actions[from.id]?.overnight ? actions[from.id].nights || 1 : 0;
    const plannedDeparture = addCalendarDays(plannedDate, nights);
    const connectionDate = stationDateTime(nextDeparture(from, previous, actions, plannedDate), from).date ?? plannedDeparture;
    const latestDeparture = addCalendarDays(plannedDeparture, Math.max(0, calendarDaysBetween(plannedDeparture, connectionDate)));
    // A manual connection may be shorter than the automatic two-hour stop.
    if (leg.departureDate < plannedDeparture || leg.departureDate > latestDeparture) {
      reasons.push(`Booked for ${leg.departureDate}, now on day ${day}`);
    }
  }
  const previousArrival = legArrivalInstant(previous, from);
  let departure = leg.departureInstant;
  if (!departure && leg.departureDate && leg.departureTime) departure = journeyInstant(`${leg.departureDate}T${leg.departureTime}:00`, from);
  if (previousArrival && departure && Date.parse(departure) < Date.parse(previousArrival)) {
    reasons.push(`Departs ${leg.departureTime} before previous arrival ${previous?.arrivalTime}`);
  }
  return reasons;
}

/**
 * Groups station IDs into day-coloured segments.
 * Day boundaries = manual overnight stops + overnight journey arrivals.
 * Exported for MapView's search-route preview.
 */
export function computeDaySegments(
  stationIds: string[],
  dayBoundaryIds: Map<string, number>,
  startDay: number = 1,
): DaySegment[] {
  if (stationIds.length === 0) return [];
  const segments: DaySegment[] = [];
  let currentDay = startDay;
  let currentSegment: string[] = [];
  for (const id of stationIds) {
    if (dayBoundaryIds.has(id) && currentSegment.length > 0) {
      segments.push({ day: currentDay, stationIds: currentSegment, color: DAY_COLORS[(currentDay - 1) % DAY_COLORS.length] });
      currentDay += dayBoundaryIds.get(id)!;
      currentSegment = [id];
    } else {
      currentSegment.push(id);
    }
  }
  if (currentSegment.length > 0) {
    segments.push({ day: currentDay, stationIds: currentSegment, color: DAY_COLORS[(currentDay - 1) % DAY_COLORS.length] });
  }
  return segments;
}

function durationUntilStation(leg: TripLeg, station: CityWithTimes | undefined): string {
  if (!leg.departureTime || !station?.arrival) return leg.duration;
  const [dh, dm] = leg.departureTime.split(":").map(Number);
  const [ah, am] = station.arrival.split(":").map(Number);
  let minutes = (ah * 60 + am) - (dh * 60 + dm);
  if (station.arrivalInstant && leg.departureInstant) {
    minutes = Math.round((Date.parse(station.arrivalInstant) - Date.parse(leg.departureInstant)) / 60_000);
  }
  if (minutes < 0) minutes += 24 * 60;
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours === 0) return `${remainder}m`;
  if (remainder === 0) return `${hours}h`;
  return `${hours}h ${remainder}m`;
}

function sliceRouteCoords(leg: TripLeg, station: CityWithTimes | undefined, stationIndex: number): TripLeg["routeCoords"] {
  const coordinates = leg.routeCoords;
  if (!coordinates || coordinates.length < 2) return coordinates;
  if (station) {
    const exactIndex = coordinates.findIndex(([latitude, longitude]) => Math.abs(latitude - station.latitude) < 0.000001 && Math.abs(longitude - station.longitude) < 0.000001);
    if (exactIndex > 0) return coordinates.slice(0, exactIndex + 1);
    const visitIndex = leg.stationData?.findIndex(visit => visit.id === station.id) ?? -1;
    const visits = leg.stationData?.slice(0, visitIndex + 1) ?? [];
    const points = visits.filter(visit => Number.isFinite(visit.latitude) && Number.isFinite(visit.longitude))
      .map(visit => [visit.latitude, visit.longitude] as [number, number]);
    if (points.length >= 2) return points;
  }
  const coordinateIndex = Math.max(1, Math.round(stationIndex / Math.max(1, leg.stations.length - 1) * (coordinates.length - 1)));
  return coordinates.slice(0, coordinateIndex + 1);
}

/** Slice a saved TripLeg at a mid-route station, keeping the A→M portion.
 *  The M→B gap is left for ghost legs to auto-fill. */
function sliceTripLeg(leg: TripLeg, midStationId: string): TripLeg {
  const midIdx = leg.stations.indexOf(midStationId);
  if (midIdx <= 0) return leg;

  const slicedStations = leg.stations.slice(0, midIdx + 1);
  const slicedStationIds = new Set(slicedStations);
  const midStation = leg.stationData?.find((s) => s.id === midStationId);
  const midArrival = midStation?.arrival;

  return {
    ...leg,
    toStationId: midStationId,
    arrivalTime: midArrival || leg.arrivalTime,
    arrivalDate: stationDateTime(midStation?.arrivalInstant, midStation!).date ?? leg.departureDate,
    arrivalInstant: midStation?.arrivalInstant,
    price: null, // The original through-fare does not price this shorter journey.
    duration: durationUntilStation(leg, midStation),
    stations: slicedStations,
    stationData: leg.stationData?.filter((s) => slicedStationIds.has(s.id)),
    routeCoords: sliceRouteCoords(leg, midStation, midIdx),
    isGhostLeg: false,
    ghostError: undefined,
    ghostLoading: undefined,
  };
}

/** Auto-add the departure city first if trip is being started */
function ensureFromStation(prev: City[], fromStation: City | null): City[] {
  if (fromStation && !prev.some((s) => s.id === fromStation.id)) return [...prev, fromStation];
  return prev;
}

// ─── Trip Planner Hook ──────────────────────────────────────────────────
// Encapsulates all trip-builder state and handlers extracted from App.tsx.
// Does NOT depend on route-search state — handlers that need fromStation
// receive it as an argument, avoiding circular dependencies.

export function useTripPlanner(
  travelDate: string,
  stopActions: StopActions,
  setStopActions: React.Dispatch<React.SetStateAction<StopActions>>,
  setSelectedStop: React.Dispatch<React.SetStateAction<City | null>>
) {
  const [tripStops, setTripStops] = useState<City[]>([]);
  const tripStopsRef = useRef(tripStops);
  tripStopsRef.current = tripStops;

  const [tripName, setTripName] = useState("My Trip");
  const [tripPanelVisible, setTripPanelVisible] = useState(false);
  const [tripLegs, setTripLegs] = useState<TripLeg[]>([]);
  const tripLegsRef = useRef(tripLegs);
  tripLegsRef.current = tripLegs;

  // ─── Recompute day assignments + segments in one pass ─────────
  // Day boundaries come from: 1) manual overnight stops, 2) overnight arrival (date comparison).
  // Stamps each leg with dayNumber/dayColor and produces tripDaySegments together.

  const tripDaySegments = useMemo(() => {
    if (tripStops.length === 0) return [];
    const ids = tripStops.map((s) => s.id);

    // Collect day-boundary station IDs → number of days to advance
    const boundaries = new Map<string, number>();

    let previous: TripLeg | undefined;
    let day = 1;
    for (let i = 0; i < ids.length - 1; i++) {
      const leg = tripLegs.find(l => l.fromStationId === ids[i] && l.toStationId === ids[i + 1]);
      const waitingDays = departureDayAdvance(leg, previous, stopActions[ids[i]], addCalendarDays(travelDate, day - 1));
      const journeyDays = leg && isOvernightLeg(leg) ? calendarDaysBetween(leg.departureDate!, leg.arrivalDate!) : 0;
      if (waitingDays + journeyDays > 0) boundaries.set(ids[i + 1], waitingDays + journeyDays);
      day += waitingDays + journeyDays;
      previous = leg;
    }

    return computeDaySegments(ids, boundaries);
  }, [tripStops, stopActions, tripLegs, travelDate]);

  // Build station → {day, color} lookup from segments (consumed by MapView)
  const tripStationDayMap = useMemo(() => {
    const map = new Map<string, { color: string; day: number }>();
    for (const seg of tripDaySegments) {
      for (const sid of seg.stationIds) map.set(sid, { color: seg.color, day: seg.day });
    }
    return map;
  }, [tripDaySegments]);

  // Stamp legs with dayNumber/dayColor from the computed segments
  useEffect(() => {
    if (tripDaySegments.length === 0 || tripLegs.length === 0) return;

    const updated = tripLegs.map((leg) => {
      const info = tripStationDayMap.get(leg.fromStationId);
      if (!info) return leg;
      const index = tripStops.findIndex(s => s.id === leg.fromStationId);
      const previous = index > 0 ? tripLegs.find(l => l.fromStationId === tripStops[index - 1].id && l.toStationId === leg.fromStationId) : undefined;
      const day = info.day + departureDayAdvance(leg, previous, stopActions[leg.fromStationId], addCalendarDays(travelDate, info.day - 1));
      const color = DAY_COLORS[(day - 1) % DAY_COLORS.length];
      return leg.dayNumber === day && leg.dayColor === color ? leg : { ...leg, dayNumber: day, dayColor: color };
    });

    if (updated.some((l, i) => l !== tripLegs[i])) setTripLegs(updated);
  }, [tripDaySegments, tripLegs, tripStationDayMap, tripStops, stopActions, travelDate]);

  // ─── Derived state ──────────────────────────────────────────────

  const tripStopIds = useMemo(
    () => new Set(tripStops.map((s) => s.id)),
    [tripStops]
  );

  // ─── Stale-leg detector ─────────────────────────────────────────
  // After reorder, surviving manual legs may have dates/times that no longer
  // match their new day position or conflict with the preceding leg's arrival.
  // Returns Map<"fromId→toId", reason> for legs that need attention.
  const staleLegKeys = useMemo(() => {
    const stale = new Map<string, string>();
    if (tripStops.length < 2 || tripLegs.length === 0) return stale;

    let previousLeg: TripLeg | undefined;

    for (let i = 0; i < tripStops.length - 1; i++) {
      const fromId = tripStops[i].id;
      const toId = tripStops[i + 1].id;
      const leg = tripLegs.find(
        (l) => l.fromStationId === fromId && l.toStationId === toId
      );

      if (!leg || leg.isGhostLeg || leg.ghostLoading) {
        previousLeg = leg;
        continue;
      }

      const reasons = staleLegReasons(leg, previousLeg, tripStops[i], tripStationDayMap.get(fromId)?.day ?? 1, stopActions, travelDate);

      if (reasons.length > 0) {
        stale.set(`${fromId}\u2192${toId}`, reasons.join("; "));
      }

      previousLeg = leg;
    }

    return stale;
  }, [tripStops, tripLegs, tripStationDayMap, travelDate, stopActions]);

  // Retain stale choices in persisted data, but treat them as unresolved for
  // map rendering and automatic onward scheduling.
  const mapTripLegs = useMemo(() => tripLegs.map((leg): TripLeg => {
    if (!staleLegKeys.has(`${leg.fromStationId}\u2192${leg.toStationId}`)) return leg;
    return {
      ...leg, requiresSelection: true, isGhostLeg: true,
      departureTime: "", arrivalTime: "", duration: "", operator: "",
      departureDate: undefined, arrivalDate: undefined, departureInstant: undefined, arrivalInstant: undefined,
      stations: [], stationData: undefined, routeCoords: undefined, price: null, isSleeper: false,
      ghostLoading: false, ghostError: false, ghostThrottled: false, ghostScheduleKey: undefined,
    };
  }), [tripLegs, staleLegKeys]);

  // ─── Handlers ───────────────────────────────────────────────────

  /** (Internal) Add a station to the trip stops list.
   *  Called by toggle handlers (food/overnight/fika) — not a direct user action.
   *  Pass fromStation to auto-add departure city.
   *  Enriches Supabase data async after the stop appears in the list. */
  const handleAddToTrip = useCallback(
    (station: City, fromStation: City | null = null) => {
      setTripStops((prev) => {
        const updated = ensureFromStation(prev, fromStation);
        if (updated.some((s) => s.id === station.id)) return updated;
        return [...updated, station];
      });
      setTripPanelVisible(true);

      // Async enrichment — patch stop in-place once Supabase data is resolved
      void enrichTripStop(station, setTripStops);
      if (fromStation) void enrichTripStop(fromStation, setTripStops);
    },
    []
  );

  const handleRemoveFromTrip = useCallback((index: number) => {
    const removedId = tripStopsRef.current[index]?.id;
    if (removedId) {
      setTripLegs((legs) => legs.filter(
        (l) => l.fromStationId !== removedId && l.toStationId !== removedId
      ));
    }
    setTripStops((prev) => prev.filter((_, i) => i !== index));
    if (removedId) setStopActions(prev => {
      const next = { ...prev }; delete next[removedId]; return next;
    });
  }, [setStopActions]);

  const handleReorderStop = useCallback((index: number, direction: "up" | "down") => {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    const stops = tripStopsRef.current;
    if (targetIndex < 0 || targetIndex >= stops.length) return;

    const movedId = stops[index]?.id;
    const neighborId = stops[targetIndex]?.id;

    // Remove legs involving either swapped stop — ghost legs will auto-refetch
    if (movedId && neighborId) {
      setTripLegs((legs) => legs.filter(
        (l) => l.fromStationId !== movedId && l.toStationId !== movedId
            && l.fromStationId !== neighborId && l.toStationId !== neighborId
      ));
    }

    setTripStops((prev) => {
      const next = [...prev];
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  }, []);

  const handleSetNights = useCallback((stationId: string, nights: number) => {
    if (!Number.isInteger(nights) || nights < 1 || nights > 365) return;
    setStopActions((prev) => ({
      ...prev,
      [stationId]: { ...(prev[stationId] || { ...DEFAULT_STOP_ACTIONS }), nights },
    }));

  }, []);

  const handleClearTrip = useCallback(() => {
    setTripStops([]);
    setTripLegs([]);
    setTripName("My Trip");
    setStopActions({});
    setSelectedStop(null);
  }, [setStopActions, setSelectedStop]);

  const handleCloseTripPanel = useCallback(() => {
    setTripPanelVisible(false);
  }, []);

  const handleToggleTripPanel = useCallback(() => {
    setTripPanelVisible((v) => !v);
  }, []);

  const handleTripStopClick = useCallback(
    (station: City) => {
      setSelectedStop(station);
    },
    [setSelectedStop]
  );

  const handleExportTrip = useCallback(() => {
    if (tripStops.length === 0) return;
    generateTripPdf({ tripName, travelDate, stops: tripStops, stopActions, tripLegs });
  }, [tripStops, tripName, travelDate, stopActions, tripLegs]);

  /** Toggle a stop action (food/overnight/fika). Implicitly adds station to trip when turning on.
   *  If the station is mid-leg (inside a saved TripLeg), slices the leg and leaves a gap for ghost legs. */
  const handleToggleAction = useCallback(
    (actionKey: "food" | "overnight" | "fika", selectedStop: City, fromStation: City | null = null) => {
      let splitInfo: { dest?: City; customDate?: string; customTime?: string } = {};

      if (!selectedStop) return splitInfo;

      // Read current action state from the live prop (avoid nesting side-effects inside setStopActions)
      const current = stopActions[selectedStop.id] || { ...DEFAULT_STOP_ACTIONS };

      if (!current[actionKey]) {
        // Turning on — check if this station is mid-leg in an existing TripLeg
        const hostLeg = tripLegsRef.current.find((leg) =>
          leg.stationData?.some((s) => s.id === selectedStop.id) &&
          leg.fromStationId !== selectedStop.id &&
          leg.toStationId !== selectedStop.id
        );

        if (hostLeg) {
          // Slice: keep A→M as a real leg, leave M→B gap for ghost legs
          const sliced = sliceTripLeg(hostLeg, selectedStop.id);
          setTripLegs((prevLegs) => [
            ...prevLegs.filter((l) => !(
              l.fromStationId === hostLeg.fromStationId &&
              l.toStationId === hostLeg.toStationId
            )),
            sliced,
            {
              fromStationId: selectedStop.id, toStationId: hostLeg.toStationId,
              routeId: `choose-${selectedStop.id}-${hostLeg.toStationId}`,
              departureTime: "", arrivalTime: "", duration: "", operator: "", stations: [],
              isGhostLeg: true, requiresSelection: true,
            },
          ]);
          // Insert station between A and B in tripStops
          setTripStops((prevStops) => {
            const fromIdx = prevStops.findIndex((s) => s.id === hostLeg.fromStationId);
            if (fromIdx < 0 || prevStops.some((s) => s.id === selectedStop.id)) return prevStops;
            const arr = [...prevStops];
            arr.splice(fromIdx + 1, 0, selectedStop);
            return arr;
          });
          // Async enrichment for the spliced-in stop
          void enrichTripStop(selectedStop, setTripStops);

          // Capture the destination and compute the offset time for the new search
          const dest = tripStopsRef.current.find((s) => s.id === hostLeg.toStationId);
          if (dest && sliced.arrivalTime) {
            const instant = nextDeparture(selectedStop, sliced, {
              ...stopActions, [selectedStop.id]: { ...current, [actionKey]: true },
            }, travelDate);
            const local = stationDateTime(instant, selectedStop);
            splitInfo = { dest, customDate: local.date, customTime: local.time };
          }
        } else {
          // Normal: append to trip
          setTripStops((prevStops) => {
            const updated = ensureFromStation(prevStops, fromStation);
            if (updated.some((s) => s.id === selectedStop.id)) return updated;
            return [...updated, selectedStop];
          });
          // Async enrichment
          void enrichTripStop(selectedStop, setTripStops);
        }
        setTripPanelVisible(true);
      }

      // Flat updater — no nested side-effects
      setStopActions((prev) => ({
        ...prev,
        [selectedStop.id]: { ...(prev[selectedStop.id] || { ...DEFAULT_STOP_ACTIONS }), [actionKey]: !current[actionKey] },
      }));

      return splitInfo;
    },
    [setStopActions, stopActions, travelDate]
  );

  const handleToggleFood = useCallback(
    (selectedStop: City, fromStation: City | null = null) => handleToggleAction("food", selectedStop, fromStation),
    [handleToggleAction]
  );
  const handleToggleOvernight = useCallback(
    (selectedStop: City, fromStation: City | null = null) => handleToggleAction("overnight", selectedStop, fromStation),
    [handleToggleAction]
  );
  const handleToggleFika = useCallback(
    (selectedStop: City, fromStation: City | null = null) => handleToggleAction("fika", selectedStop, fromStation),
    [handleToggleAction]
  );

  /** Add a route (journey) to the trip — stores the journey metadata.
   *  Silently replaces any ghost leg for the same from→to pair. */
  const handleAddRouteToTrip = useCallback(
    (route: TrainRoute, fromStation: City | null = null, overrideFromId?: string, overrideToId?: string) => {
      // Add departure station to the trip if needed
      setTripStops((prev) => {
        return ensureFromStation(prev, fromStation);
      });

      // Use explicit trip-stop IDs when provided (they match tripStops[i].id),
      // otherwise fall back to route.from / route.to (HAFAS EVA IDs).
      const legFromId = overrideFromId || route.from;
      const legToId = overrideToId || route.to;

      // Prefer the route's pre-resolved coords (captured during search from fresh
      // HAFAS data) over re-resolving from stationStore, which can lose coords
      // when station data gets merged.  Fall back to stationStore only if needed.
      const routeCoords = route.routeCoords && route.routeCoords.length >= 2
        ? route.routeCoords
        : resolveRouteCoords(route.stations);
      if (import.meta.env.DEV) {
        console.log(
          `[TripLeg] ${legFromId}→${legToId} | route.stations: ${route.stations.length} | resolved coords: ${routeCoords.length}`
        );
      }

      const newLeg = trainRouteToTripLeg(route, {
        fromStationId: legFromId,
        toStationId: legToId,
        routeCoords,
        isGhostLeg: false,
      });

      // Store the journey leg metadata — replace any ghost leg for this pair
      setTripLegs((prev) => {
        // Remove ghost leg for this pair + skip exact duplicates
        const filtered = prev.filter(
          (l) => l.routeId !== route.id && (l.fromStationId !== legFromId || l.toStationId !== legToId)
        );
        return [...filtered, newLeg];
      });
      setTripPanelVisible(true);
    },
    []
  );

  /** Add station to trip and mark it as the next departure point. */
  const handleContinueFrom = useCallback(
    (station: City, fromStation: City | null = null) => {
      setTripStops((prev) => {
        let updated = ensureFromStation(prev, fromStation);
        if (!updated.some((s) => s.id === station.id)) {
          updated = [...updated, station];
        }
        return updated;
      });
      setTripPanelVisible(true);
    },
    []
  );

  // ─── Ghost legs — auto-fetch connections for consecutive stops ──
  useGhostLegs(tripStops, stopActions, mapTripLegs, setTripLegs, travelDate, tripLegs);

  return {
    tripStops,
    setTripStops,
    tripName,
    setTripName,
    tripPanelVisible,
    setTripPanelVisible,
    tripStopIds,
    tripDaySegments,
    tripStationDayMap,
    tripLegs,
    mapTripLegs,
    setTripLegs,
    handleAddToTrip,
    handleAddRouteToTrip,
    handleRemoveFromTrip,
    handleReorderStop,
    handleSetNights,
    handleClearTrip,
    handleCloseTripPanel,
    handleToggleTripPanel,
    handleTripStopClick,
    handleExportTrip,
    handleToggleFood,
    handleToggleOvernight,
    handleToggleFika,
    handleContinueFrom,
    staleLegKeys,
  };
}
